<template>
  <div id="app">
    <div v-if="isMaintenanceMode" class="h-screen w-full flex justify-center items-center">
      <div class="absolute top-7">
        <img width="200" src="./assets/img/plexytrade-logo.svg" />
      </div>
      <div class="w-[96%] lg:w-1/2 max-w-xl bg-white py-12 rounded-lg shadow-md px-4 lg:px-8">
        <img width="70" src="./assets/img/warning.png" class="mx-auto mb-5" />
        <h1 class="text-3xl text-blue text-center font-semibold font-display">{{ $t('under_maintenance') }}</h1>
        <p class="text-gray-900 text-base leading-7 mt-4 text-center" v-html="$t('under_maintenance_text')"></p>
      </div>
    </div>
    <div v-if="!isMaintenanceMode" class="md:flex">
      <!-- Sidebar for mobile -->
      <side-bar
        v-if="$route.meta.showSideBar || is404RouteAuth"
        :becomeAnIBModal="becomeAnIBModal"
        :isSidebarOpen="isSidebarOpen"
        :isLive="isLive"
        :isIB="isIB"
        :filteredMenu="filteredMenu"
        ref="sidebar"
        @openTradingCentralPopup="openTradingCentralPopup"
        :TCDeposits="totalTradingCentralDeposits"
      ></side-bar>
      <!-- Overlay to disable main content when sidebar is open -->
      <div v-if="isSidebarOpen" class="fixed inset-0 bg-opacity-50 z-10 md:hidden" @click="toggleSidebar"></div>

      <!-- Header, Main, and Footer Container -->
      <div
        ref="container"
        class="w-full md:overflow-auto bg-gray-200 relative"
        :class="[
          {
            'ltr:md:ml-64 rtl:md:mr-64 ltr:md:pl-10 ltr:md:pr-6 rtl:md:pr-10 rtl:md:pl-6 3xl:flex 3xl:justify-around': ($auth.check() && $route.meta.showSideBar) || is404RouteAuth,
          },
        ]"
      >
        <div
          :class="[
            { 'max-w-[1400px] w-[99%]': ($auth.check() && $route.meta.showSideBar) || is404RouteAuth },
            { 'overflow-auto': isIOS },
            { marginLeft: ($auth.check() && $route.meta.showSideBar) || is404RouteAuth },
          ]"
        >
          <div class="flex flex-col md:flex-row justify-between w-full items-center" :class="[{ 'grid grid-cols-3-default': showCashbackUnavailableBanner }, {'md:mt-2': showCashbackUnavailableBanner && (showDemoUpgradeBanner || showAccountApprovalBanner) }]">
            <div
              v-if="showCashbackUnavailableBanner"
              class="flex flex-col gap-2 absolute md:relative top-24 md:top-0 left-7 right-7 md:left-auto md:right-auto w-auto md:w-full maxLg:order-2 maxLg:col-span-3 col-span-2"
            >
              <div
                v-if="showDemoUpgradeBanner"
                class="h-11 w-full flex gap-1 items-center justify-around border border-solid border-blue font-body text-blue text-sm font-semibold py-2 px-4 rounded-lg bg-[#20B3811A] hover:bg-[#20B38140] cursor-pointer"
              >
                <router-link :to="'/' + Language + '/register/upgrade'" class="font-body text-blue text-sm font-semibold ltr:mr-3 rtl:ml-3">{{
                  $t('upgrade_to_live_account').tTitle()
                }}</router-link>
                <div class="border-r-2 border-b-2 border-solid border-blue inline-block p-0.5 -rotate-45"></div>
              </div>
              <div
                v-if="showAccountApprovalBanner"
                @click="firstLogin(uploadDocumentsURL)"
                class="w-max max-w-full flex gap-3 items-center justify-center border border-solid border-blue font-body text-blue text-sm font-semibold py-3 px-4 rounded-lg bg-[#20B3811A] hover:bg-[#20B38140] cursor-pointer whitespace-nowrap"
              >
                {{ DocCount == 0 ? $t('upload_documents_for_account_approval').tTitle() : $t('account_approval_pending').tTitle() }}
                <div class="border-r-2 border-b-2 border-solid border-blue inline-block p-0.5 -rotate-45 shrink-0"></div>
              </div>
              <div class="w-full rounded-lg bg-orange bg-opacity-10 border border-orange px-5 py-3 flex items-center gap-3">
                <img src="@/assets/img/cashback/account-unavailable.svg" :alt="$t('cashback_account_unavailable')" class="w-6 h-6 shrink-0" />
                <p class="font-body font-semibold text-[14px] text-orange leading-relaxed min-w-0" v-html="$t('cashback_account_unavailable', { URL: contactURL })"></p>
              </div>
            </div>

            <!-- Single account banners on other pages (original layout) -->
            <div
              v-else-if="showDemoUpgradeBanner"
              class="h-11 absolute md:relative top-24 md:top-2 w-full md:max-w-[225px] max-w-[310px] flex gap-1 items-center justify-around border border-solid border-blue font-body text-blue text-sm font-semibold py-2 px-4 rounded-lg bg-[#20B3811A] hover:bg-[#20B38140] cursor-pointer"
              :class="[{ 'top-40 md:inset-0': $route.meta.usesSubMenuItems }]"
            >
              <router-link :to="'/' + Language + '/register/upgrade'" class="font-body text-blue text-sm font-semibold ltr:mr-3 rtl:ml-3">{{
                $t('upgrade_to_live_account').tTitle()
              }}</router-link>
              <div class="border-r-2 border-b-2 border-solid border-blue inline-block p-0.5 -rotate-45"></div>
            </div>
            <div
              v-else-if="showAccountApprovalBanner"
              @click="firstLogin(uploadDocumentsURL)"
              class="absolute md:relative top-24 md:top-0 w-max max-w-[calc(100%-2rem)] md:max-w-none flex gap-3 items-center justify-center border border-solid border-blue font-body text-blue text-sm font-semibold py-3 px-4 rounded-lg bg-[#20B3811A] hover:bg-[#20B38140] cursor-pointer whitespace-nowrap"
              :class="[
                DocCount != 0 ? 'md:max-w-[213px]' : '',
                { 'top-40 md:inset-0': $route.meta.usesSubMenuItems && !$route.meta.identifierPath.includes('copy-trading') },
                { '!top-28 md:!top-0': !isVerified && $route.name === 'Deposit' },
              ]"
            >
              {{ DocCount == 0 ? $t('upload_documents_for_account_approval').tTitle() : $t('account_approval_pending').tTitle() }}
              <div class="border-r-2 border-b-2 border-solid border-blue inline-block p-0.5 -rotate-45 shrink-0"></div>
            </div>

            <!-- Header -->
            <header-bar
              v-if="$route.meta.showHeader || is404RouteAuth"
              :toggleSidebar="toggleSidebar"
              :isSidebarOpen="isSidebarOpen"
              :logout="logout"
              :isVerified="isVerified"
              :clientInfo="userInfoObject"
              :auth="$auth.check()"
              :isEmailVerified="isEmailVerified.verified"
              :class="{ 'maxLg:col-span-3 maxLg:order-1': showCashbackUnavailableBanner }"
            ></header-bar>
          </div>

          <!-- Main Content -->
          <router-view
            :platformImages="platformImages"
            :class="routeClasses"
            :style="{ 'max-width': ($auth.check() && $route.meta.showSideBar) || is404RouteAuth ? containerWidth : '' }"
          ></router-view>
        </div>
      </div>
      <!-- FOOTER -->
      <footer-collapsible
        v-if="$route.meta.footer || is404RouteAuth"
        class="fixed bottom-0 z-[11] w-full"
        :class="[
          { '3xl:flex 3xl:justify-around ltr:md:pl-[19rem] rtl:md:pr-[19rem] ltr:md:pr-8 rtl:md:pl-8': $route.meta.showSideBar || is404RouteAuth },
          { 'flex justify-center md:pl-0 md:pr-0': !$route.meta.showSideBar },
        ]"
        :customClass="[
          { 'relative max-w-[1384px] w-[99%]': $route.meta.showSideBar || is404RouteAuth },
          { 'max-w-[964px] 2xl:w-full w-full relative': !$route.meta.showSideBar },
          { 'md:w-[80%]': is404RouteAuth },
        ]"
      />
    </div>

    <div v-if="show2FAPopup && $auth.check()" class="fixed flex justify-center items-center inset-0 z-50 bg-gray-900 bg-opacity-70 w-full h-full overflow-y-auto">
      <div class="bg-white rounded-lg w-11/12 lg:w-[642px] max-w-[642px] maxMd:mt-20 m-auto relative">
        <button @click="show2FAPopup = false" class="bg-white rounded-full h-10 w-10 absolute -top-14 ltr:right-0 rtl:left-0 appearance-none p-0">
          <div class="h-6 w-0.5 bg-blue rotate-45 ltr:ml-5 rtl:mr-5"></div>
          <div class="h-6 w-0.5 bg-blue -rotate-45 -mt-6 ltr:ml-5 rtl:mr-5"></div>
        </button>
        <div class="maxMd:px-5 p-12 overflow-y-auto">
          <img src="./assets/img/two-factor-popup-icon.svg" width="115" class="mb-11" />
          <h4 class="text-gray-900 text-[40px] leading-[46px] font-display font-bold mb-8">{{ $t('two_factor_authentication') }}</h4>
          <p class="text-base text-gray-900 leading-8" v-html="$t('two_factor_authentication_popup_text')"></p>
        </div>
      </div>
    </div>
    <BecomeIBModal :becomeAnIBModalActive="becomeAnIBModalActive" :zoomScale="zoomScale" @close-modal="becomeAnIBModalActive = false" @update-ib="updateToAnIB" />
    <div class="md:hidden">
      <BottomSheet :isOpen="ShowCompetitionBanner && $route.meta.showSideBar" height="auto" @close="closeCompetitionBanner">
        <div class="flex flex-col items-center justify-center">
          <img src="./assets/img/icons/competitions/trading-competition-dashboard-graphic.svg" width="160" height="123" alt="" />
          <div class="text-center pt-2 mb-5">
            <h2 class="text-lg text-blue pb-1">{{ $t('trading_comp') }}</h2>
            <p class="text-sm text-gray-900 font-medium pb-3">{{ $t('trading_comp_slogan') }}</p>
            <button class="py-1 bg-green rounded-md text-white appearance-none font-medium text-sm text-center w-40 cursor-pointer">
              <router-link :to="'/' + Language + '/competitions'">
                {{ $t('enter_competition') }}
              </router-link>
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
    <div v-if="showTradingCentralPopup" class="fixed flex justify-center items-center inset-0 z-50 bg-gray-900 bg-opacity-70 w-full h-full overflow-y-auto">
      <div class="bg-white rounded-lg w-11/12 lg:w-[642px] max-w-[642px] m-auto relative">
        <button @click="showTradingCentralPopup = false" class="bg-white rounded-full h-10 w-10 absolute -top-14 ltr:right-0 rtl:left-0 appearance-none p-0">
          <div class="h-6 w-0.5 bg-blue rotate-45 ltr:ml-5 rtl:mr-5"></div>
          <div class="h-6 w-0.5 bg-blue -rotate-45 -mt-6 ltr:ml-5 rtl:mr-5"></div>
        </button>
        <div class="maxMd:px-5 sm:ps-12 pt-12 sm:py-12">
          <trading-central-component :isPopup="true" @hide="showTradingCentralPopup = false" />
        </div>
      </div>
    </div>

    <logo-loader v-if="logoLoaderActive" :text="logoLoaderText"></logo-loader>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { service } from './main.js'
import config from './config'
import api from './api'
import FooterCollapsible from './components/FooterCollapsible.vue'
import LogoLoader from './components/LogoLoader.vue'
import BottomSheet from './components/reusableComponents/BottomSheet.vue'
import scaleDirective from '@/directives/scaleDirective'
import links from './config/links'
import SideBar from './components/SideBar.vue'
import HeaderBar from './components/HeaderBar.vue'
import BecomeIBModal from './components/BecomeIBModal.vue'
import TradingCentralComponent from './components/TradingCentralComponent.vue'

// load platform images
const pathToLogos = require.context('./assets/img/logos', true)
const platformImages = { mt4: '', mt5: '', pamm: '' }
let platNames = Object.keys(platformImages)
for (let i = 0, l = platNames.length; i < l; i++) {
  platformImages[platNames[i]] = pathToLogos('./' + platNames[i] + '-small.png', true)
}

export default {
  directives: {
    scaleDirective,
  },
  components: {
    FooterCollapsible,
    LogoLoader,
    SideBar,
    HeaderBar,
    BecomeIBModal,
    BottomSheet,
    TradingCentralComponent,
  },
  data() {
    return {
      parentHeading: '',
      heading: '',
      platformImages,
      isRegistration: false,
      isPaymentRedirect: false,
      becomeAnIBModalActive: false,
      userHeadMenuActive: false,
      currencySymbols: config.currencies,
      newAppVersion: false,
      status: {
        incomplete: 'Incomplete',
        pending: 'Pending',
        full: 'Approved',
      },
      scheduleModal: false,
      sideMenuVisibility: false,
      isMaintenanceMode: config.setMaintenanceMode,
      show2FAPopup: false,
      isSidebarOpen: false,
      showCompetitionBanner: false,
      timeoutId: null,
      showTradingCentralPopup: false,
      totalWithdrawals: 0,
      totalDeposits: 0,
      totalTradingCentralDeposits: 0,
      overflowing: false,
      containerWidth: '100%',
      marginLeft: '',
    }
  },
  head: {
    title: function () {
      return {
        inner: this.heading,
      }
    },
  },
  watch: {
    Language(l) {
      if (l === null || typeof l === 'undefined') {
        return
      }
      let url = location.pathname.split('/')
      if (url[1] !== l) {
        url[1] = l
        this.$locale.change(l)
        window.localStorage.setItem('lang', l)
        this.$router.push(url.join('/'))
      }
    },
    UserInfo: {
      immediate: true,
      deep: true,
      handler(u) {
        if (u.ID <= 0) {
          return
        }
        if (typeof u.first_name === 'undefined') {
          this.getUserDetails()
        }
      },
    },
    $route: {
      immediate: true,
      handler(route) {
        this.isRegistration = typeof this.$route.meta.registerComponent !== 'undefined'
        this.ensureReferralCodePersistence()
        this.$bus.$emit('LiveFeedConnect')
        if (this.User.accountTypes.length) {
          this.getParentMenu(route, false)
        }
        this.routeClasses
        this.$nextTick(() => {
          this.checkOverflow()
        })
      },
      isVerified: 'routeClasses',
      isLive: 'routeClasses',
      isDemo: 'routeClasses',
      isRegistration: 'routeClasses',
      DocCount: 'routeClasses',
    },
    becomeAnIBModalActive(value) {
      if (value === true) {
        this.disableScroll()
      }
      if (value === false) {
        this.enableScroll()
      }
    },
    isSidebarOpen(val) {
      if (val === true) {
        this.disableScroll()
      }
      if (val === false) {
        this.enableScroll()
      }
    },
    '$i18n.locale': function () {
      this.setDirection()
    },
    'User.accountTypes': function () {
      if (this.User.accountTypes.length > 0) {
        this.getParentMenu(this.$route)
        this.isLive
        this.isDemo
      }
    },
    totalTradingCentralDeposits: function () {
      if (this.totalTradingCentralDeposits >= 5000) {
        this.$router.getRoutes().forEach(route => {
          if (route.name === 'Tools') {
            route.meta.needsPrivileges = ['live']
            this.getParentMenu(route)
          }
        })
      }
    },
    ShowCompetitionBanner(newValue) {
      if (newValue) {
        // Clear any existing timeout to avoid conflicts
        clearTimeout(this.timeoutId)
        this.timeoutId = setTimeout(() => {
          this.showCompetitionBanner = true
        }, 5000) // 5-seconds delay
      } else {
        // Clear the timeout if the value is false
        clearTimeout(this.timeoutId)
        this.timeoutId = null
        this.showCompetitionBanner = false
      }
    },
  },
  computed: {
    coName() {
      if (this.User.info.client && this.User.info.client.company_name !== undefined) {
        return this.User.info.client.company_name
      } else return ''
    },
    filteredMenu() {
      if (!this.Menu[0].children.length) {
        return []
      }
      // remove competitions from us countries
      let filtered = this.Menu[0].children.filter(item => !item.meta || item.meta.skipMenu !== true)
      if (config.eligibleCountriesTradingCompetition.includes(this.User.info.country_code)) {
        filtered = filtered.filter(item => item.meta.T !== 'competitions')
      }
      return filtered
    },
    isVerified() {
      let isVerified
      if (this.User && this.User.info && this.User.info.client) {
        const accountState = this.User.info.client.account_state
        const accountVerified = this.User.info.client.verified
        if (accountState === 'full' && accountVerified) {
          isVerified = true
        } else if (accountState === 'rejected' || accountState === 'pending' || !accountVerified) {
          isVerified = false
        }
      }
      this['user/setIsVerified'](isVerified)
      return isVerified
    },

    userInfoFirstName() {
      if (this.User && this.User.info) {
        if (this.User.info.first_name && this.User.info.last_name) {
          return this.User.info.first_name + ' ' + this.User.info.last_name
        } else if (this.User.info.client && this.User.info.client.company_name) {
          return this.User.info.client.company_name
        }
      }
      return '' // Return empty string if no match found
    },

    uploadDocumentsURL() {
      return '/' + this.Language + '/profile/upload-documents'
    },
    downloadURL() {
      return '/' + this.Language + '/trading-account/download'
    },
    contactURL() {
      return links.getLink('contact', 'en')
    },
    partnersAgreement() {
      return links.getLink('partnersAgreement', 'en')
    },
    clientFullName() {
      return (this.User.info.first_name + ' ' + this.User.info.last_name).tTitle()
    },
    ShowCompetitionBanner() {
      return (
        this.$store.getters['competition/showCompetitionBanner'](this.$route.path) &&
        !this.logoLoaderActive &&
        !config.eligibleCountriesTradingCompetition.includes(this.User.info.country_code) &&
        this.isLive
      )
    },

    zoomScale() {
      return window.devicePixelRatio
    },
    userInfoObject() {
      // Here we return an array with userInfoFirstName (or multiple names if needed)
      return {
        userInfoFirstName: this.userInfoFirstName,
        first_name: this.User.info.first_name,
        last_name: this.User.info.last_name,
        coName: this.coName,
        emailAddress: this.User.info.email,
      }
    },
    isVerifyEmailRoute() {
      // Check if the current route name is 'Verify Email'
      return this.$route.name === 'Verify Email'
    },
    is404RouteAuth() {
      return this.$route.name === '404' && this.$auth.check()
    },
    routeClasses() {
      const classes = []

      if (this.$route.meta.showSideBar) {
        classes.push(...this.getSideBarClasses())
      }

      if (this.$route.meta.usesSubMenuItems) {
        classes.push(...this.getSubMenuClasses())
      } else {
        classes.push(...this.getNonSubMenuClasses())
      }

      return classes
    },
    isIOS() {
      return /iPhone|iPod/.test(navigator.userAgent)
    },
    cashbackBlocked() {
      if (!this.UserInfo.client) return false
      return this.UserInfo.client.cashback_status === 2
    },
    showDemoUpgradeBanner() {
      return this.isDemo && !this.isRegistration && !this.isVerifyEmailRoute && this.$route.name !== 'Login'
    },
    showAccountApprovalBanner() {
      return !this.isVerified && this.isLive && !this.isVerifyEmailRoute && this.$route.name !== 'Login'
    },
    showCashbackUnavailableBanner() {
      return this.$route.name === 'Cashback' && (this.cashbackBlocked || !this.isVerified)
    },

    ...mapGetters({
      Language: 'language/code',
      User: 'user/all',
      UserInfo: 'user/info',
      DocCount: 'user/docCount',
      isEmailVerified: 'user/isEmailVerified',
      Menu: 'menu/all',
      logoLoaderActive: 'logoLoader/active',
      logoLoaderText: 'logoLoader/text',
      BonusConfig: 'bonusconfig/all',
      accountTypes: 'user/accountTypes',
      isDemo: 'user/isDemo',
      isLive: 'user/isLive',
      isIB: 'user/isIB',
      // TotalTCDeposited: 'user/totalTCDeposited',
    }),
  },
  created() {
    this.isRegistration = typeof this.$route.meta.registerComponent !== 'undefined'
    this.$bus.$on('ChangeLang', this.changeLang)
    this.$bus.$on('ChangeHeadings', this.changeHeadings)
    this.$bus.$on('InnerRouteChange', this.checkOverflow)
    this.$bus.$on('PaymentRedirect', this.redirectToPayment)
    // this.$bus.$on('forceUpdate', this.$forceUpdate())
  },
  mounted() {
    if (this.isMaintenanceMode) {
      document.title = 'Under Maintenance | Plexytrade'
    }

    if (this.$getCookie('disableMaintenance') == 'yes') {
      this.isMaintenanceMode = false
    }

    // this.newAppVersion = true
    if (window.localStorage) {
      this.checkAppVersion()
      setInterval(() => {
        this.checkAppVersion()
      }, 10000)
    }

    this.$el.addEventListener('click', this.docClick)
    // Add event listener for when the user closes the browser tab to check if it is a remote user.
    // If so, the user should logout.
    window.addEventListener('beforeunload', this.logoutIfRemoteUser)

    this.getBonusConfig()
    this.setDirection()
    document.addEventListener('click', this.closeSidebarOnClickOutside)

    this.checkOverflow()
    window.addEventListener('resize', this.checkOverflow)

    // Track campaign and set a cookie for it
    if (this.$route.query.campaign_name !== undefined && window.localStorage.getItem('campaign_name') === null) {
      window.localStorage.setItem('campaign_name', this.$route.query.campaign_name)
      window.location.reload() //reload in order for the campaign_name to become available in main.js
    }
  },
  beforeDestroy() {
    this.$el.removeEventListener('click', this.docClick)
    // remove event listener for closing the browser  tab.
    window.removeEventListener('beforeunload', this.logoutIfRemoteUser)
    this.$bus.$off('ChangeLang', this.changeLang)
    this.$bus.$off('ChangeHeadings', this.changeHeadings)
    this.$bus.$off('InnerRouteChange', this.checkOverflow)
    this.$bus.$off('PaymentRedirect', this.redirectToPayment)
    // this.$bus.$off('forceUpdate', this.$forceUpdate())
    document.removeEventListener('click', this.closeSidebarOnClickOutside)
  },
  methods: {
    // Capture `t` from the URL, persist it, then strip it from the URL.
    // Storage (cookie/localStorage/sessionStorage) is the source of truth after that.
    ensureReferralCodePersistence() {
      const IBQueryParam = this.$route.query.t
      if (!IBQueryParam) return

      this.$setReferralCode(IBQueryParam)

      const query = { ...this.$route.query }
      delete query.t
      this.$router
        .replace({
          path: this.$route.path,
          query,
        })
        .catch(() => {})
    },
    closeSidebarOnClickOutside(event) {
      // Check if the click was outside the sidebar
      if (this.isSidebarOpen && !this.$refs.sidebar.$el.contains(event.target)) {
        this.isSidebarOpen = false
      }
    },
    openTradingCentralPopup() {
      this.showTradingCentralPopup = true
    },
    setDirection() {
      const html = document.documentElement
      const lang = this.$i18n.locale
      html.setAttribute('lang', lang)
      if (lang === 'fa') {
        html.setAttribute('dir', 'rtl')
      } else {
        html.setAttribute('dir', 'ltr')
      }
    },
    getBonusConfig() {
      service.get('/bonus/config').then(response => {
        var bonusConfig = {
          depositBonus: {
            enabled: response.data.deposit_bonus_enabled,
          },
          noDepositBonus: {
            enabled: response.data.no_deposit_bonus_enabled,
          },
        }

        this['bonusconfig/set'](bonusConfig)
      })
    },

    checkAppVersion() {
      let storageVersion = window.localStorage.getItem('app-version')
      let request = new XMLHttpRequest()
      request.onreadystatechange = () => {
        if (request.readyState === 4) {
          if (request.status === 200) {
            if (request.responseText) {
              let data = JSON.parse(request.responseText)
              let latestVersion = data.version
              if (storageVersion === '') {
                // if storage version is empty for some reason, just set it
                window.localStorage.setItem('app-version', latestVersion)
              } else if (storageVersion !== latestVersion) {
                // show update message
                this.newAppVersion = true
              } else {
                // this is for reactive changes(local dev) or for setting the
                // version number to previous values(not sure why we'll need that)
                this.newAppVersion = false
              }
            }
          } else {
            // 'An error occurred during your request: ' +  request.status + ' ' + request.statusText
          }
        }
      }
      request.open('get', '/static/auto-generated.json')
      request.send()
    },
    checkAllowedLink(metaData) {
      if (metaData.liveOnly == true) {
        if (this.isDemo) {
          return false
        }
      }
      return true
    },
    updateToAnIB(val) {
      // this['logoLoader/set'](true)
      service
        .post('/users/upgrade/ib', {
          userID: this.User.info.ID,
          target_region: val.ibMainTargetRegion,
          website: val.ibWebsiteSocialMedia,
        })
        .then(response => {
          if (response.data.success) {
            this.becomeAnIBModalActive = false
            api.GetUserAccountTypes().then(accountTypes => {
              this['user/setAccountTypes'](accountTypes)
              const isIB = accountTypes.indexOf('ib') !== -1
              this['user/setIsIB'](isIB)
              this.getParentMenu(this.$route)
              this.$router.push('/' + this.Language + '/partner')
              // this['logoLoader/set'](false)
            })
          }
        })
        .catch(() => {
          // this['logoLoader/set'](false)
          this.$toasted.error(this.$t('internal_error'))
        })
    },
    changeHeadings(heading, parent) {
      if (heading) {
        this.heading = heading // .tUpper()
        this.$emit('updateHead')
      }
      if (parent) {
        this.parentHeading = parent // .tUpper()
      }
    },
    docClick(e) {
      // parent emit clicks
      if (!e.target) {
        return
      }

      if (!this.isRegistration && !this.$route.meta.skipStructure) {
        // close live feed - #openTradeNewsFeedSection
        let isLiveFeed = this.$elementParentExists(e.target, document.querySelector('#liveFeed'))
        if (!isLiveFeed) {
          this.$bus.$emit('LiveFeedToggleMode')
        }
      }

      // close user dropdown
      let isUserDropDown = this.$elementParentExists(e.target, document.querySelector('.dropdown.user'))
      if (!isUserDropDown && this.userHeadMenuActive) {
        this.userHeadMenuActive = false
      }
      this.$bus.$emit('AppClick', e)
    },
    async getUserDetails() {
      if (!this.User.info.ID) {
        return
      }

      // this['logoLoader/set'](true) // Start loader
      // try {
      // Fetch user data
      const userInfoResponse = await service.get(`/users/${this.User.info.ID}`, null, 'getuserdetails')
      const userInfo = userInfoResponse.data
      userInfo.ID = this.User.info.ID

      const wallet = await api.GetWallet()
      this['user/setWallet'](wallet)

      this.getWalletTotals()

      const accountTypes = await api.GetUserAccountTypes()
      this['user/setAccountTypes'](accountTypes)

      // Check email verification status
      const emailVerificationResponse = await service.post('/email/get-verification-status', {
        email: userInfo.email,
        userID: userInfo.ID,
      })
      const isEmailVerified = emailVerificationResponse.data.verified
      // Redirect if email is not verified
      if (!isEmailVerified) {
        this.$router.push('/' + (this.Language || 'en') + '/verify-email').catch(() => {})
        // this['logoLoader/set'](false)
      }

      // Set data in store
      this['user/setInfo'](userInfoResponse.data)
      this['user/setEmailVerified'](emailVerificationResponse.data)

      const doc = await api.GetDocumentCount()
      this['user/setDocCount'](doc)

      const isDemo = accountTypes.indexOf('demo') !== -1 && accountTypes.indexOf('live') === -1
      this['user/setIsDemo'](isDemo)

      const isLive = accountTypes.indexOf('live') !== -1
      this['user/setIsLive'](isLive)
      const isIB = userInfo.client.is_ib
      this['user/setIsIB'](isIB)

      this.User.accountTypes = accountTypes

      const TA = await api.GetTradingAccounts()
      this['user/setTA'](TA.data.TAs)

      const BlockedCountries = await api.GetPSPBlockedCountries()
      this['finance/setPSPBlockedCountries'](BlockedCountries)

      api.GetCurrentCompetition()
      // this.getParentMenu(this.$route)

      // Set preferred language
      const preferredLanguage = this.User.info.preferred_language
      const localLang = window.localStorage.getItem('lang')
      if (!localLang) {
        this['language/set'](preferredLanguage)
        window.localStorage.setItem('lang', preferredLanguage)
        this.$locale.change(this.Language)
      }
      // } finally {
      //   // Stop loader
      //   // this['logoLoader/set'](false)
      // }
    },

    checkMenuPrivileges(item) {
      if (!this.accountTypes.length) {
        return
      }
      if (typeof item.meta.needsPrivileges !== 'undefined') {
        item.meta.skipMenu = true
        item.meta.needsPrivileges.forEach(privilege => {
          if (privilege === 'depositAmount') {
            if (this.totalTradingCentralDeposits >= 5000) {
              item.meta.needsPrivileges = ['live']
            }
          }
          if (this.User.accountTypes.indexOf(privilege) !== -1) return (item.meta.skipMenu = false)
          // if (this.$route.meta.T == item.meta.T) {
          //   return this.$router.push('/' + (this.Language || 'en') + '/').catch(() => {})
          // }
        })
      }
    },
    getParentMenu(route, initialLoad = true) {
      this.isSidebarOpen = false
      let menu = this.Menu
      if (!menu) {
        return
      }
      const currentRouteName = this.$getRouteName(route.name) !== '' ? this.$getRouteName(route.name) : route.name
      menu[0].children.forEach(menuItem => {
        if (initialLoad) {
          this.checkMenuPrivileges(menuItem)
        }
        menuItem.meta.active = false
        let activeParent
        if (menuItem.children) {
          menuItem.children.forEach(child => {
            child.meta.navActive = false
            if (child.name === currentRouteName) {
              this.checkMenuPrivileges(child)
              activeParent = menuItem
              this.checkMenuPrivileges(activeParent)
              child.meta.navActive = true
              activeParent.meta.active = true
              this.parentHeading = this.$t(activeParent.meta.T).tTitle()
              this.heading = this.$t(child.meta.T).tTitle()
              if (this.isRegistration) {
                this.parentHeading = this.heading
              }
              this.addMeta('ogtitle', this.heading + ' | ' + this.$t('app_name'))
              this.addMeta('description', this.$t(activeParent.meta.description))
              this.$emit('updateHead')
            }
            return
          })
        } else {
          if (menuItem.name === currentRouteName) {
            activeParent = menuItem
            this.checkMenuPrivileges(activeParent)
            activeParent.meta.active = true
            this.parentHeading = this.$t(activeParent.meta.T).tTitle()
            this.heading = this.$t(activeParent.meta.T).tTitle()
            this.addMeta('ogtitle', this.heading + ' | ' + this.$t('app_name'))
            this.addMeta('description', this.$t(activeParent.meta.description))
            this.$emit('updateHead')
          }
        }
      })
    },
    changeLang(event) {
      if (this.Language === null || typeof this.Language === 'undefined') {
        return
      }
      if (event) {
        this['language/set'](event)
      }
      // this['language/set'](this.Language)
      window.localStorage.setItem('lang', this.Language)

      // If user is logged in, save the language setting
      if (this.User.info.ID) {
        service
          .post('/users/set-language', {
            userID: this.User.info.ID,
            langCode: this.Language,
          })
          .then(response => {
            if (response.data.success) {
              // this.$toasted.success(this.$t('lang_settings_saved'))
            }
          })
          .catch(() => {
            this.$toasted.error(this.$t('unable_save_lang_settings'))
          })
      }

      this.$locale.change(this.Language)
      this.$i18n.locale = this.Language
    },
    // uddGoTo(path) {
    //   this.userHeadMenuActive = false
    //   this.$router.push(path)
    // },
    firstLogin(redir) {
      this['user/setInfo'](this.User.info)
      if (redir) {
        this.$router.push(redir)
      }
    },
    logout() {
      this.$auth
        .logout({
          makeRequest: false,
          params: {},
          // error: () => {
          // },
          redirect: '/' + this.Language + '/login',
        })
        .then(() => {
          // Success
          // this.$deleteCookie('login_license_read')
          this['user/reset']()
          this['competition/reset']()
          localStorage.removeItem('comp_banner_closed')
        })
    },
    openCompleteProfile() {
      this.$router.push('/' + this.Language + '/profile/complete-profile')
    },
    extraMenuItemChecksPassed(itemChild) {
      let menuItemChecksPassed = true

      if (itemChild.name === 'My Bonuses') {
        if (this.BonusConfig.depositBonus.enabled === false) {
          return false
        }
      }

      return menuItemChecksPassed
    },
    disableScroll() {
      document.querySelector('body').classList.add('overflow-hidden')
    },
    enableScroll() {
      document.querySelector('body').classList.remove('overflow-hidden')
    },
    /**
     * Function to logout the user if the user that is closing the tab is logged in as a remote user.
     */
    logoutIfRemoteUser() {
      // Preserve the session for this payment navigation only.
      if (this.isPaymentRedirect) {
        this.isPaymentRedirect = false
        return
      }

      let isUserRemote = this.$getCookie(`remote-user-${this.User.info.ID}`)
      // check if the user is remote
      if (isUserRemote === '1') {
        // logout and delete cookie.
        this.logout()
        this.$deleteCookie(`remote-user-${this.User.info.ID}`)
      }
    },
    redirectToPayment(url) {
      this.isPaymentRedirect = true
      try {
        window.location.assign(url)
      } catch (error) {
        this.isPaymentRedirect = false
        throw error
      }
    },
    toggleSidebar(event) {
      // Stop the event from propagating to the document listener
      event.stopPropagation()
      this.isSidebarOpen = !this.isSidebarOpen
    },
    becomeAnIBModal() {
      this.becomeAnIBModalActive = !this.becomeAnIBModalActive
    },

    getSideBarClasses() {
      // Classes for when the sidebar is shown
      return ['md:mt-5', 'mt-24', 'md:px-0', 'overflow-auto', 'md:overflow-hidden']
    },

    getSubMenuClasses() {
      // Classes for when the route uses sub-menu items
      if ((!this.isVerified && this.isLive) || (this.isDemo && !this.isRegistration)) {
        return this.DocCount === 0 ? ['maxS:mt-28'] : ['mt-24']
      }
      return ['mt-8']
    },

    getNonSubMenuClasses() {
      // Classes for when the route does not use sub-menu items
      const hasAccountBanner = this.showDemoUpgradeBanner || this.showAccountApprovalBanner
      if (hasAccountBanner) {
        if (this.showCashbackUnavailableBanner) {
          return this.DocCount === 0 ? ['maxS:mt-[19rem]'] : ['mt-56']
        }
        return this.DocCount === 0 ? ['maxS:mt-44', 'mt-40'] : ['mt-40']
      }
      if (this.showCashbackUnavailableBanner) return ['maxS:mt-[14rem]']
      return []
    },

    closeCompetitionBanner() {
      this['competition/setHasClosed'](true)
    },
    getWalletTotals() {
      service
        .get('/finance/user/' + this.User.info.ID + '/totals-converted', {})
        .then(response => {
          if (response.data.total_deposits) {
            for (let i = 0; i < response.data.total_deposits.length; i++) {
              if (response.data.total_deposits[i].currency == this.User.wallet.currency) {
                this.totalDeposits = response.data.total_deposits[i].value
                break
              }
            }
          }
          if (response.data.total_trading_central_deposits) {
            for (let i = 0; i < response.data.total_trading_central_deposits.length; i++) {
              if (response.data.total_trading_central_deposits[i].currency == this.User.wallet.currency) {
                this.totalTradingCentralDeposits = response.data.total_trading_central_deposits[i].value
                break
              }
            }
          }
          if (response.data.total_withdrawals) {
            for (let i = 0; i < response.data.total_withdrawals.length; i++) {
              if (response.data.total_withdrawals[i].currency == this.User.wallet.currency) {
                this.totalWithdrawals = response.data.total_withdrawals[i].value
                break
              }
            }
          }
          // this['logoLoader/set'](false)
          this.$store.commit('user/SET_TOTAL_DEPOSITED', this.totalDeposits)
          this.$store.commit('user/SET_TOTAL_TC_DEPOSITED', this.totalTradingCentralDeposits)
          this.$store.commit('user/SET_TOTAL_WITHDRAWALS', this.totalWithdrawals)

          // fix bug -- when Tools is locked and you type /tools on the url it redirected you to Tools. The below code redirects to Dashboard
          // if user is Dominica from Trading Central ignore the total deposits as she needs to test/check the tools
          // if ((this.totalTradingCentralDeposits < 5000 && this.User.info.ID != 220526) && this.$route.path.includes('tools')) {
          //   return this.$router.push('/' + (this.Language || 'en') + '/').catch(() => { })
          // }
        })
        .catch(() => {
          // this['logoLoader/set'](false)
        })
    },
    ...mapActions([
      'logoLoader/set',
      'language/set',
      'bonusconfig/set',
      'user/setInfo',
      'user/reset',
      'user/setAccountTypes',
      'user/setIB',
      'user/setIsDemo',
      'user/setIsLive',
      'user/setIsVerified',
      'user/setEmailVerified',
      'user/setDocCount',
      'user/setIsIB',
      'user/setWallet',
      'user/setTA',
      'user/setTotalDeposited',
      'user/setTotalTCDeposited',
      'user/setTotalWithdrawals',
      'finance/setPSPBlockedCountries',
      'competition/reset',
      'competition/setHasClosed',
      // 'resetState',
    ]),
    checkOverflow() {
      //the method checks if there is overflow-y or not,
      //if there is overflow it sets containerWidth && marginLeft and make them adjust in order to prevent the page from moving
      this.$nextTick(() => {
        const container = this.$refs.container
        if (container) {
          const isOverflowing = container.scrollHeight > container.clientHeight
          this.overflowing = isOverflowing
          this.containerWidth = isOverflowing ? '1400px' : 'calc(1400px + 17px)'
          this.marginLeft = isOverflowing ? 'sm:ml-[17px]' : 'sm:ml-0'
        }
      })
    },
  },
}
</script>

<style>
@import 'tailwindcss/base';

@import 'tailwindcss/components';

@import 'tailwindcss/utilities';

@layer base {
  /* Select */
  select {
    background-image: none;
    @apply h-12 w-full rounded font-body font-semibold text-base ltr:pl-5 ltr:pr-9 rtl:pr-5 rtl:pl-9 cursor-pointer;
  }
  .select-placeholder {
    @apply text-gray-900 text-opacity-20;
  }
  option {
    @apply text-gray-900 font-semibold text-base bg-white;
  }

  .input-error {
    @apply ring-1 ring-orange focus:ring-orange hover:ring-orange bg-gray-400;
  }

  [type='text'] {
    @apply h-12 w-full rounded-md bg-gray-100 font-body text-base text-gray-900 font-semibold px-5 placeholder:opacity-20 placeholder:text-gray-900 border-none;
  }

  [type='text']:focus {
    @apply border-none;
  }

  [type='password'] {
    @apply h-12 w-full rounded-md bg-gray-100 font-body text-base text-gray-900 font-semibold px-5 placeholder:text-gray-300 border-none;
  }

  [type='password']:focus {
    @apply border-none;
  }

  [type='checkbox'] {
    @apply w-6 h-6 bg-gray-100 rounded-md border-none outline-none shadow-none ring-offset-0 ring-0 cursor-pointer;
  }

  input[type='checkbox']:focus {
    @apply border-none outline-none ring-0 shadow-none ring-offset-0;
  }

  [type='checkbox']:checked {
    @apply border-none outline-none ring-0;
  }

  /*

  [type='radio'] {
    @apply h-28 w-32 rounded-md border-solid cursor-pointer;
  }

  [type='radio']:focus {
    @apply border-none outline-none ring-0;
  }

  [type='radio']:checked {
    @apply border-none outline-none ring-0;
  } */
}

@layer components {
  button.btn,
  input.btn {
    @apply bg-blue text-white font-semibold font-body px-8 py-4 rounded-lg;
  }
  button.btn.btn-green,
  input.btn.btn-green {
    @apply bg-green;
  }
  button.btn:disabled,
  input.btn:disabled {
    @apply bg-gray-500 text-gray-300 cursor-not-allowed;
  }

  .form-check {
    @apply flex items-center;
  }

  input.checkbox {
    @apply h-6 w-6 border border-gray-400 bg-gray-400 rounded-md checked:bg-blue checked:border-blue focus:outline-none transition duration-200 align-top bg-no-repeat bg-center bg-contain ltr:mr-2 rtl:ml-2 cursor-pointer;
  }
  label.checkbox-label {
    @apply inline-block font-body text-base text-gray-900;
  }
  label.checkbox-label-xs {
    @apply inline-block font-body text-xs text-gray-900;
  }
  label.checkbox-label-required {
    @apply text-red-error;
  }
  /* .select-arrow {
    @apply pointer-events-none absolute inset-y-0 ltr:right-0 rtl:left-0 flex items-center px-2;
  }
  .select-arrow svg {
    @apply fill-current h-6 w-6 text-blue;
  } */
}

/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
.toasted-container {
  z-index: 49 !important;
}
.toasted-container .toasted.toasted-primary {
  min-width: 200px;
  max-width: 400px;
  border-radius: 12px;
  padding: 15px 20px;
  box-shadow: none;
  font-size: 15px;
  line-height: 23px;
}

.toasted-container .toasted.toasted-primary.success {
  background: #20b381;
}

.toasted-container .toasted.toasted-primary.error {
  background: #bb3b3b;
}

.toasted-container .toasted.toasted-primary.info {
  background: #2b63e3;
}

@media (min-width: 601px) {
  [dir='rtl'] .toasted-container.top-right {
    top: 10%;
    left: 7%;
    right: auto;
  }
}

@font-face {
  font-family: 'Nexa';
  src: url('/public/static/fonts/NexaBold/NexaBold.eot');
  src:
    url('/public/static/fonts/NexaBold/NexaBold.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NexaBold/NexaBold.woff2') format('woff2'),
    url('/public/static/fonts/NexaBold/NexaBold.woff') format('woff'),
    url('/public/static/fonts/NexaBold/NexaBold.ttf') format('truetype'),
    url('/public/static/fonts/NexaBold/NexaBold.svg#NexaBold') format('svg');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Nexa';
  src: url('/public/static/fonts/NexaLight/NexaLight.eot');
  src:
    url('/public/static/fonts/NexaLight/NexaLight.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NexaLight/NexaLight.woff2') format('woff2'),
    url('/public/static/fonts/NexaLight/NexaLight.woff') format('woff'),
    url('/public/static/fonts/NexaLight/NexaLight.ttf') format('truetype'),
    url('/public/static/fonts/NexaLight/NexaLight.svg#NexaLight') format('svg');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Nexa';
  src: url('/public/static/fonts/NexaRegular/NexaRegular.eot');
  src:
    url('/public/static/fonts/NexaRegular/NexaRegular.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NexaRegular/NexaRegular.woff2') format('woff2'),
    url('/public/static/fonts/NexaRegular/NexaRegular.woff') format('woff'),
    url('/public/static/fonts/NexaRegular/NexaRegular.ttf') format('truetype'),
    url('/public/static/fonts/NexaRegular/NexaRegular.svg#NexaRegular') format('svg');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Noto Sans';
  src: url('/public/static/fonts/NotoSans-Bold/NotoSans-Bold.eot');
  src:
    url('/public/static/fonts/NotoSans-Bold/NotoSans-Bold.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NotoSans-Bold/NotoSans-Bold.woff2') format('woff2'),
    url('/public/static/fonts/NotoSans-Bold/NotoSans-Bold.woff') format('woff'),
    url('/public/static/fonts/NotoSans-Bold/NotoSans-Bold.ttf') format('truetype'),
    url('/public/static/fonts/NotoSans-Bold/NotoSans-Bold.svg#NotoSans-Bold') format('svg');
  font-weight: bold;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Noto Sans';
  src: url('/public/static/fonts/NotoSans-BoldItalic/NotoSans-BoldItalic.eot');
  src:
    url('/public/static/fonts/NotoSans-BoldItalic/NotoSans-BoldItalic.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NotoSans-BoldItalic/NotoSans-BoldItalic.woff2') format('woff2'),
    url('/public/static/fonts/NotoSans-BoldItalic/NotoSans-BoldItalic.woff') format('woff'),
    url('/public/static/fonts/NotoSans-BoldItalic/NotoSans-BoldItalic.ttf') format('truetype'),
    url('/public/static/fonts/NotoSans-BoldItalic/NotoSans-BoldItalic.svg#NotoSans-BoldItalic') format('svg');
  font-weight: bold;
  font-style: italic;
  font-display: swap;
}

@font-face {
  font-family: 'Noto Sans';
  src: url('/public/static/fonts/NotoSans-Italic/NotoSans-Italic.eot');
  src:
    url('/public/static/fonts/NotoSans-Italic/NotoSans-Italic.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NotoSans-Italic/NotoSans-Italic.woff2') format('woff2'),
    url('/public/static/fonts/NotoSans-Italic/NotoSans-Italic.woff') format('woff'),
    url('/public/static/fonts/NotoSans-Italic/NotoSans-Italic.ttf') format('truetype'),
    url('/public/static/fonts/NotoSans-Italic/NotoSans-Italic.svg#NotoSans-Italic') format('svg');
  font-weight: normal;
  font-style: italic;
  font-display: swap;
}

@font-face {
  font-family: 'Noto Sans';
  src: url('/public/static/fonts/NotoSans-Regular/NotoSans-Regular.eot');
  src:
    url('/public/static/fonts/NotoSans-Regular/NotoSans-Regular.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NotoSans-Regular/NotoSans-Regular.woff2') format('woff2'),
    url('/public/static/fonts/NotoSans-Regular/NotoSans-Regular.woff') format('woff'),
    url('/public/static/fonts/NotoSans-Regular/NotoSans-Regular.ttf') format('truetype'),
    url('/public/static/fonts/NotoSans-Regular/NotoSans-Regular.svg#NotoSans') format('svg');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Noto Sans';
  src: url('/public/static/fonts/NotoSans-Medium/NotoSans-Medium.eot');
  src:
    url('/public/static/fonts/NotoSans-Medium/NotoSans-Medium.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NotoSans-Medium/NotoSans-Medium.woff2') format('woff2'),
    url('/public/static/fonts/NotoSans-Medium/NotoSans-Medium.woff') format('woff'),
    url('/public/static/fonts/NotoSans-Medium/NotoSans-Medium.ttf') format('truetype'),
    url('/public/static/fonts/NotoSans-Medium/NotoSans-Medium.svg#NotoSans-Medium') format('svg');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

@font-face {
  font-family: 'Noto Sans';
  src: url('/public/static/fonts/NotoSans-SemiBold/NotoSans-SemiBold.eot');
  src:
    url('/public/static/fonts/NotoSans-SemiBold/NotoSans-SemiBold.eot?#iefix') format('embedded-opentype'),
    url('/public/static/fonts/NotoSans-SemiBold/NotoSans-SemiBold.woff2') format('woff2'),
    url('/public/static/fonts/NotoSans-SemiBold/NotoSans-SemiBold.woff') format('woff'),
    url('/public/static/fonts/NotoSans-SemiBold/NotoSans-SemiBold.ttf') format('truetype'),
    url('/public/static/fonts/NotoSans-SemiBold/NotoSans-SemiBold.svg#NotoSans-SemiBold') format('svg');
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}

</style>
